<template>
  <v-container fluid>
    <v-alert v-if="errorMessage" type="error" text class="mb-4" role="alert">
      <div class="d-flex flex-wrap align-center justify-space-between">
        <span>{{ errorMessage }}</span>
        <v-btn text color="error" min-height="44" @click="loadData">Retry</v-btn>
      </div>
    </v-alert>
    <!-- Top Summary Stats -->
    <v-row v-if="!errorMessage" dense>
      <v-col
        v-for="stat in stats"
        :key="stat.label"
        cols="12"
        sm="6"
        md="3"
      >
        <v-card outlined class="pa-4 d-flex align-center">
          <v-avatar size="40" tile class="mr-4" :color="stat.color" dark>
            <v-icon>{{ stat.icon }}</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="subtitle-2 grey--text">{{ stat.label }}</div>
            <div v-if="isLoading">
              <v-skeleton-loader type="text" width="60" />
            </div>
            <div v-else class="font-weight-bold text-h6">
              {{ formatValue(stat.value, stat.isCurrency) }}
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Bottom Highlight Cards -->
    <v-row v-if="!errorMessage" class="mt-4" dense>
      <v-col cols="12" sm="4">
        <v-card color="indigo lighten-1" dark class="pa-4 rounded-xl">
          <div class="subtitle-2">Net Profit</div>
          <div class="d-flex align-center justify-space-between mt-2">
            <v-icon size="40">mdi-cash-multiple</v-icon>
            <div v-if="isLoading">
              <v-skeleton-loader type="text" width="100" />
            </div>
            <div v-else class="text-h5 font-weight-bold">
              {{ NetProfit | currency }}
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4">
        <v-card color="deep-purple lighten-1" dark class="pa-4 rounded-xl">
          <div class="subtitle-2">Tax Collected</div>
          <div class="d-flex align-center justify-space-between mt-2">
            <v-icon size="40">mdi-receipt</v-icon>
            <div v-if="isLoading">
              <v-skeleton-loader type="text" width="100" />
            </div>
            <div v-else class="text-h5 font-weight-bold">
              {{ TaxCollected | currency }}
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="4">
        <v-card color="pink darken-1" dark class="pa-4 rounded-xl">
          <div class="subtitle-2">Discounts</div>
          <div class="d-flex align-center justify-space-between mt-2">
            <v-icon size="40">mdi-sale</v-icon>
            <div v-if="isLoading">
              <v-skeleton-loader type="text" width="100" />
            </div>
            <div v-else class="text-h5 font-weight-bold">
              {{ Discounts | currency }}
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import locationService from "@/modules/Location/service.js";

export default {
  props: ["dateTime", "id"],
  data() {
    return {
      isLoading: true,
      errorMessage: "",
      Revenue: 0,
      Discounts: 0,
      InvoiceCount: 0,
      customerCount: 0,
      NetSales: 0,
      expense: 0,
      NetProfit: 0,
      income: 0,
      TaxCollected: 0,
      cashAmount: 0,
      cardAmount: 0,
      otherPayment: 0,
      cashInHand: 0,
    };
  },
  computed: {
    stats() {
      return [
        {
          label: this.$t("labels.invoiceCount"),
          value: this.InvoiceCount,
          icon: "mdi-file-document-outline",
          color: "blue lighten-2",
        },
        {
          label: this.$t("labels.customerCount"),
          value: this.customerCount,
          icon: "mdi-account-multiple",
          color: "green lighten-1",
        },
        {
          label: this.$t("labels.revenue"),
          value: this.Revenue,
          icon: "mdi-cash",
          color: "teal",
          isCurrency: true,
        },
        {
          label: this.$t("labels.income"),
          value: this.income,
          icon: "mdi-wallet",
          color: "orange darken-1",
          isCurrency: true,
        },
        {
          label: this.$t("labels.cash"),
          value: this.cashAmount,
          icon: "mdi-cash-100",
          color: "purple lighten-1",
          isCurrency: true,
        },
        {
          label: this.$t("labels.card"),
          value: this.cardAmount,
          icon: "mdi-credit-card",
          color: "cyan darken-1",
          isCurrency: true,
        },
        {
          label: this.$t("labels.otherPayments"),
          value: this.otherPayment,
          icon: "mdi-bank-transfer",
          color: "indigo lighten-2",
          isCurrency: true,
        },
        {
          label: this.$t("labels.expense"),
          value: this.expense,
          icon: "mdi-cash-minus",
          color: "red lighten-1",
          isCurrency: true,
        },
        {
          label: this.$t("labels.cashInHand"),
          value: this.cashInHand,
          icon: "mdi-hand-coin",
          color: "brown lighten-1",
          isCurrency: true,
        },
      ];
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    formatValue(value, isCurrency = false) {
      if (this.isLoading) return "--";
      if (isCurrency) return this.$options.filters.currency(value);
      return this.nFormatter(value, 1);
    },
    async loadData() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        const response = await locationService.Reports.getLocationSales({
          start: this.dateTime.start,
          end: this.dateTime.end,
          LocationId: this.id,
        });
          this.Revenue = parseFloat(response.data.revenue);
          this.InvoiceCount = response.data.invoiceCount;
          this.Discounts = parseFloat(response.data.discounts);
          this.NetSales = parseFloat(response.data.netSales);
          this.NetProfit = parseFloat(response.data.netProfit);
          this.TaxCollected = parseFloat(response.data.taxCollected);
          this.cashAmount = parseFloat(response.data.cashAmount);
          this.otherPayment = parseFloat(response.data.otherPaymentAmount);
          this.customerCount = parseFloat(response.data.customerCount);
          this.expense = parseFloat(response.data.expense);
          this.cardAmount =
            parseFloat(response.data.creditCardAmount) +
            parseFloat(response.data.debitCardAmount);
          this.income = this.otherPayment + this.cashAmount + this.cardAmount;
          this.cashInHand =
            parseFloat(this.cashAmount) - parseFloat(this.expense);
      } catch (error) {
        this.errorMessage = "Performance data could not be loaded for this period.";
      } finally {
        this.isLoading = false;
      }
    },
    nFormatter(num, digits) {
      if (!num) return 0;
      const si = [
        { value: 1, symbol: "" },
        { value: 1e3, symbol: "k" },
        { value: 1e6, symbol: "M" },
        { value: 1e9, symbol: "B" },
      ];
      const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
      let i;
      for (i = si.length - 1; i > 0; i--) {
        if (num >= si[i].value) break;
      }
      return (num / si[i].value).toFixed(digits).replace(rx, "$1") + si[i].symbol;
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}
</style>
